import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";

const HandEyeCurvePlay = () => {
  const canvasRef  = useRef(null);
  const requestRef = useRef(null);
  const navigate   = useNavigate();

  const [userPath,  setUserPath]  = useState([]);
  const [finishMsg, setFinishMsg] = useState(null);

  const endLineCoordsRef  = useRef([]);
  const outsideStartRef   = useRef(null);
  const ballRef           = useRef({ x: 0, y: 0, size: 10 });
  const startTimeRef      = useRef(null);
  const nextSampleRef     = useRef(0);
  const lastXRef          = useRef(0);
  const lastTimeRef       = useRef(Date.now());
  const penaltyRef        = useRef(0);
  const finishedRef       = useRef(false);
  const startedRef        = useRef(false);
  const pathPointsOuter   = useRef([]);
  const pathPointsInner   = useRef([]);

  useEffect(() => {
    init();
    requestRef.current = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(requestRef.current);
  }, []);

  const init = () => {
    const canvas = canvasRef.current;

    // ── KEY FIX: innerWidth/innerHeight matches CSS 100vw/100vh exactly ──
    canvas.width  = window.innerWidth;
    canvas.height = window.innerHeight;

    const w = canvas.width;
    const h = canvas.height;

    pathPointsOuter.current = [
      { x: 0.05*w, y: 0.50*h },
      { x: 0.20*w, y: 0.80*h },
      { x: 0.35*w, y: 0.35*h },
      { x: 0.50*w, y: 0.80*h },
      { x: 0.65*w, y: 0.35*h },
      { x: 0.80*w, y: 0.80*h },
      { x: 0.92*w, y: 0.50*h }
    ];

    pathPointsInner.current = pathPointsOuter.current.map(p => ({ x: p.x, y: p.y - 100 }));

    const start = pathPointsOuter.current[0];
    const end   = pathPointsInner.current[0];

    ballRef.current = { x: (start.x+end.x)/2, y: (start.y+end.y)/2, size: 10 };

    lastXRef.current        = ballRef.current.x;
    lastTimeRef.current     = Date.now();
    penaltyRef.current      = 0;
    outsideStartRef.current = null;
    startedRef.current      = false;
    finishedRef.current     = false;
  };

  const drawSmooth = (ctx, points) => {
    ctx.beginPath();
    ctx.moveTo(points[0].x, points[0].y);
    for (let i = 1; i < points.length - 1; i++) {
      const xc = (points[i].x + points[i+1].x) / 2;
      const yc = (points[i].y + points[i+1].y) / 2;
      ctx.quadraticCurveTo(points[i].x, points[i].y, xc, yc);
    }
    const last = points.length - 1;
    ctx.quadraticCurveTo(points[last-1].x, points[last-1].y, points[last].x, points[last].y);
    ctx.stroke();
  };

  const draw = () => {
    const canvas = canvasRef.current;
    const ctx    = canvas.getContext("2d");
    const w      = canvas.width;
    const h      = canvas.height;

    ctx.clearRect(0, 0, w, h);
    drawSmooth(ctx, pathPointsOuter.current);
    drawSmooth(ctx, pathPointsInner.current);

    ctx.setLineDash([6, 6]);
    ctx.beginPath();
    ctx.moveTo(pathPointsInner.current[0].x, pathPointsInner.current[0].y);
    ctx.lineTo(pathPointsOuter.current[0].x, pathPointsOuter.current[0].y);
    ctx.stroke();
    ctx.setLineDash([]);

    const last = pathPointsOuter.current.length - 1;
    ctx.beginPath();
    ctx.moveTo(pathPointsInner.current[last].x, pathPointsInner.current[last].y);
    ctx.lineTo(pathPointsOuter.current[last].x,  pathPointsOuter.current[last].y);
    ctx.strokeStyle = "red"; ctx.stroke(); ctx.strokeStyle = "black";

    const ball = ballRef.current;
    ctx.beginPath();
    ctx.arc(ball.x, ball.y, ball.size, 0, Math.PI * 2);
    ctx.fillStyle = "blue"; ctx.fill();

    ctx.fillStyle = "black"; ctx.font = "20px Arial";
    ctx.fillText("Move in clockwise direction", w/2 - 150, 40);
  };

  const getInput = () => {
    const gp = navigator.getGamepads()[0];
    if (!gp) return { dx: 0, dy: 0, active: false, r2: 0, l2: 0 };

    let dx = gp.axes[2] ?? gp.axes[3] ?? 0;
    let dy = gp.axes[1] ?? 0;
    const DZ = 0.2;
    dx = Math.abs(dx) > DZ ? dx : 0;
    dy = Math.abs(dy) > DZ ? dy : 0;

    return { dx, dy, active: dx !== 0 || dy !== 0, r2: gp.buttons[7]?.value ?? 0, l2: gp.buttons[6]?.value ?? 0 };
  };

  const update = () => {
    if (finishedRef.current) return;

    const input  = getInput();
    const ball   = ballRef.current;
    const canvas = canvasRef.current;
    const now    = Date.now();
    const w      = canvas.width;
    const h      = canvas.height;

    const rawDt = now - lastTimeRef.current;
    const dt    = Math.min(rawDt / 16.67, 2);
    lastTimeRef.current = now;

    if (!startedRef.current && input.active) {
      startTimeRef.current  = now;
      nextSampleRef.current = now + 250;
      startedRef.current    = true;
    }

    const accel = input.active ? 1 + input.r2 * 2 : 1;

    if (input.l2 > 0.05) {
      ball.x += input.dx * 5 * 0.4 * dt;
      ball.y += input.dy * 5 * 0.4 * dt;
    } else if (input.active) {
      ball.x += input.dx * 5 * accel * dt;
      ball.y += input.dy * 5 * accel * dt;
    }

    // clamp — matches visible screen exactly
    const r = ball.size;
    ball.x = Math.max(r, Math.min(w - r, ball.x));
    ball.y = Math.max(r, Math.min(h - r, ball.y));

    const dx = ball.x - lastXRef.current;
    lastXRef.current = ball.x;

    if (startedRef.current && now >= nextSampleRef.current) {
      setUserPath(p => [...p, [ball.x, ball.y]]);
      nextSampleRef.current = now + 250;
    }

    // penalty uses canvas ratios
    const isOutside = ball.y < h * 0.35 || ball.y > h * 0.75;
    if (isOutside) {
      if (!outsideStartRef.current) outsideStartRef.current = now;
    } else {
      if (outsideStartRef.current) {
        penaltyRef.current     += (now - outsideStartRef.current) / 1000;
        outsideStartRef.current = null;
      }
    }

    const endX = pathPointsOuter.current[pathPointsOuter.current.length - 1].x;
    if (ball.x >= endX && dx > 0.5 && !finishedRef.current) {
      finishedRef.current = true;
      finishGame();
    }
  };

  const finishGame = () => {
    const endTime = Date.now();
    const testTime = (endTime - startTimeRef.current) / 1000;
    if (outsideStartRef.current) { penaltyRef.current += (endTime - outsideStartRef.current) / 1000; outsideStartRef.current = null; }
    const penaltyTime = penaltyRef.current || 0;
    const totalTime   = testTime + penaltyTime;

    const result = {
      GameName: "Curved Path", userPath,
      testTime: Number(testTime.toFixed(2)), penaltyTime: Number(penaltyTime.toFixed(2)), totalTime: Number(totalTime.toFixed(2)),
      innerCurveCoordsList: pathPointsInner.current || [], outerCurveCoordsList: pathPointsOuter.current || [],
      endLineCoords: endLineCoordsRef.current || [], ballSize: ballRef.current.size,
      StartBallX: ballRef.current.x, StartBallY: ballRef.current.y
    };

    setFinishMsg({ time: result.totalTime, testTime: result.testTime, penaltyTime: result.penaltyTime, score: result });

    axios.post("/save-handeye", {
      email: localStorage.getItem("user_email"), device: "Gamepad", data: { CurvedPathData: result }
    }).catch(err => console.error("Save error:", err));
  };

  const loop = () => { update(); draw(); requestRef.current = requestAnimationFrame(loop); };

  return (
    <div style={{ overflow: "hidden", width: "100vw", height: "100vh", margin: 0, padding: 0 }}>
      <canvas ref={canvasRef} style={{ position: "fixed", top: 0, left: 0, width: "100vw", height: "100vh", display: "block", background: "#fff" }} />

      {finishMsg && (
        <div style={{ position: "fixed", top: 0, left: 0, width: "100%", height: "100%", background: "rgba(0,0,0,0.6)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 999 }}>
          <div style={{ background: "white", padding: "40px 48px", borderRadius: "16px", textAlign: "center", minWidth: "300px", boxShadow: "0 8px 32px rgba(0,0,0,0.3)" }}>
            <h2 style={{ margin: "0 0 8px", fontSize: "24px", color: "#222" }}>Test Complete!</h2>
            <p style={{ color: "#666", marginBottom: "20px", fontSize: "15px" }}>Curve Path finished</p>
            <div style={{ background: "#f5f5f5", borderRadius: "10px", padding: "16px", marginBottom: "24px" }}>
              <p style={{ margin: "4px 0", fontSize: "14px", color: "#555" }}>Test time: <b>{finishMsg.testTime}s</b></p>
              <p style={{ margin: "4px 0", fontSize: "14px", color: "#d32f2f" }}>Penalty time: <b>{finishMsg.penaltyTime}s</b></p>
              <p style={{ margin: "12px 0 0", fontSize: "20px", fontWeight: "bold", color: "#222" }}>Total: {finishMsg.time}s</p>
            </div>
            <button onClick={() => { setFinishMsg(null); navigate("/hand-eye/result", { state: { currentScore: finishMsg.time } }); }}
              style={{ padding: "12px 40px", background: "#4CAF50", color: "white", border: "none", borderRadius: "10px", fontSize: "18px", fontWeight: "bold", cursor: "pointer" }}>
              OK
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default HandEyeCurvePlay;
